// token 刷新器：401 后的单飞（single-flight）刷新逻辑，纯函数便于单测。
// 多个并发 401 只发起一次 /auth/refresh，共享同一个 in-flight Promise。
import { buildExpiresAt } from './session.mjs'

/**
 * 创建 token 刷新器。
 * @param {object} options
 * @param {() => object|null} options.getSession 读取当前会话
 * @param {(session: object) => object} options.saveSession 持久化新会话并返回
 * @param {(refreshToken: string) => Promise<object>} options.requestRefresh 调用刷新接口，返回 { access_token, refresh_token, expires_in }
 * @param {() => void} [options.onRefreshFailed] 刷新失败时的清理回调（清 session、打开登录弹窗）
 * @returns {{ refreshSession: () => Promise<object>, hasInFlight: () => boolean }}
 */
export function createTokenRefresher(options) {
  const {
    getSession,
    saveSession,
    requestRefresh,
    onRefreshFailed = () => {}
  } = options || {}

  let inFlightPromise = null

  // 关键节点：并发调用共享同一个刷新 Promise，避免重复刷新
  function refreshSession() {
    if (inFlightPromise) {
      return inFlightPromise
    }

    const session = getSession()

    // 逻辑分支：没有会话或缺少 refresh_token 时无法续期，直接判定过期
    if (!session || !session.refreshToken) {
      onRefreshFailed()
      return Promise.reject(new Error('登录已过期，请重新登录'))
    }

    inFlightPromise = requestRefresh(session.refreshToken)
      .then(tokens => {
        const nextSession = saveSession({
          ...session,
          accessToken: tokens.access_token,
          refreshToken: tokens.refresh_token,
          expiresIn: tokens.expires_in,
          expiresAt: buildExpiresAt(tokens.expires_in)
        })
        return nextSession
      })
      .catch(error => {
        // 关键节点：刷新失败后立即清理会话并通知页面打开登录弹窗
        onRefreshFailed()
        throw error
      })
      .finally(() => {
        // 关键节点：无论成败都清空 in-flight，下次 401 允许重新尝试刷新
        inFlightPromise = null
      })

    return inFlightPromise
  }

  return {
    refreshSession,
    hasInFlight: () => Boolean(inFlightPromise)
  }
}
